import React, { useState, useEffect, useCallback } from 'react';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { NotificationsActive as SystemIcon } from '@mui/icons-material';
import { UserAvatar } from '@/components/common/UserAvatar';
import { UniversalPagination } from '../components/common/UniversalPagination';
import { PageCard } from '@/components/common/PageCard';
import { PageHint } from '@/components/common/PageHint';
import { PageToolbar } from '../components/common/PageToolbar';
import { useNotifications } from '../hooks/useNotifications';
import { Notification } from '../types/notification';
import { NotificationDetailModal } from '../components/notifications/NotificationDetailModal';
import { GetNotificationsParams } from '../services/notification.service';
import { useAppSelector, useAppDispatch, selectBadges } from '@/store';
import { updateBadge } from '@/store/updates-hub/updatesHubSlice';

const getChipColor = (level: 'info' | 'warning' | 'danger') => {
  switch (level) {
    case 'info':    return 'info';
    case 'warning': return 'warning';
    case 'danger':  return 'error';
    default:        return 'default';
  }
};

const NotificationsPageContent: React.FC = () => {
  const {
    notifications,
    totalPages,
    currentPage,
    totalCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const badges = useAppSelector(selectBadges);
  const dispatch = useAppDispatch();

  const [sourceTab, setSourceTab] = useState<'' | 'system' | 'expert'>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState<'' | 'info' | 'warning' | 'danger'>('');
  const [filterReadStatus, setFilterReadStatus] = useState<'' | 'read' | 'unread'>('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const [apiParams, setApiParams] = useState<GetNotificationsParams>({
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    fetchNotifications(apiParams);
  }, [apiParams, fetchNotifications]);

  const updateApiParams = useCallback((newParams: Partial<GetNotificationsParams>) => {
    setApiParams(prev => ({ ...prev, ...newParams, page: newParams.page ?? 1 }));
  }, []);

  const handleRowClick = async (notification: Notification) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);
        if (badges.notifications > 0) {
          dispatch(updateBadge({ key: 'notifications', value: badges.notifications - 1 }));
        }
      } catch {
        // ignore
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      dispatch(updateBadge({ key: 'notifications', value: 0 }));
      await fetchNotifications(apiParams);
    } catch {
      // ignore
    }
  };

  if (error) {
    return (
      <Box>
        <Typography color="error">Помилка: {error}</Typography>
        <Button onClick={() => fetchNotifications(apiParams)} sx={{ mt: 2 }}>
          Спробувати знову
        </Button>
      </Box>
    );
  }

  const unreadCount = badges.notifications;

  return (
    <Box data-i-doc={PageIndices.NOTIFICATIONS}>
      <PageHint iDoc={PageIndices.NOTIFICATIONS} />

      <PageCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, px: 2 }}>
            <ToggleButtonGroup
                value={sourceTab}
                exclusive
                size="small"
                onChange={(_, val) => {
                    if (val !== null) {
                        setSourceTab(val);
                        updateApiParams({ source: val || undefined, page: 1 });
                    }
                }}
            >
                <ToggleButton value="">Усі</ToggleButton>
                <ToggleButton value="system">Системні</ToggleButton>
                <ToggleButton value="expert">Від експертів</ToggleButton>
            </ToggleButtonGroup>
            
            <Box sx={{ flexGrow: 1 }} />
            
            {unreadCount > 0 && (
                <Button variant="outlined" onClick={handleMarkAllAsRead} disabled={loading.action}>
                  Позначити всі як прочитані
                </Button>
            )}
        </Box>

        <PageToolbar
          storageKey="filters:notifications"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за повідомленням',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); updateApiParams({ search: v || undefined, page: 1 }); },
              flexGrow: 1,
            },
            {
              id: 'level',
              type: 'select',
              label: 'Тип',
              value: filterLevel,
              onChange: (v) => { setFilterLevel(v); updateApiParams({ level: v || undefined, page: 1 }); },
              options: [
                { value: 'info',    label: 'Інформація' },
                { value: 'warning', label: 'Попередження' },
                { value: 'danger',  label: 'Небезпека' },
              ],
              minWidth: '160px',
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: filterReadStatus,
              onChange: (v) => { setFilterReadStatus(v); updateApiParams({ status: v || undefined, page: 1 }); },
              options: [
                { value: 'read',   label: 'Прочитані' },
                { value: 'unread', label: 'Непрочитані' },
              ],
              minWidth: '150px',
            },
          ]}
          onReset={() => {
            setSourceTab('');
            setSearchQuery('');
            setFilterLevel('');
            setFilterReadStatus('');
            updateApiParams({ source: undefined, search: undefined, level: undefined, status: undefined, page: 1 });
          }}
        />

        {loading.notifications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 180 }}>Відправник</TableCell>
                    <TableCell>Повідомлення</TableCell>
                    <TableCell sx={{ width: 110 }}>Рівень</TableCell>
                    <TableCell sx={{ width: 150 }}>Дата</TableCell>
                    <TableCell sx={{ width: 120 }}>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography sx={{ mt: 2 }}>Сповіщень не знайдено.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications.map((notification) => (
                      <TableRow
                        key={notification.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: notification.isRead ? 'inherit' : 'action.selected',
                        }}
                        onClick={() => handleRowClick(notification)}
                      >
                        <TableCell>
                          {notification.source === 'expert' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <UserAvatar
                                name={notification.senderName ?? undefined}
                                sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}
                              />
                              <Typography variant="body2" noWrap>
                                {notification.senderName}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'action.selected',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <SystemIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                              </Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                Платформа
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: notification.isRead ? 'normal' : 600 }}>
                          {notification.message}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={notification.level}
                            color={getChipColor(notification.level)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(notification.createdAt).toLocaleString('uk-UA')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={notification.isRead ? 'Прочитано' : 'Нове'}
                            color={notification.isRead ? 'default' : 'primary'}
                            variant={notification.isRead ? 'outlined' : 'filled'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <UniversalPagination
              page={currentPage}
              count={totalPages}
              limit={apiParams.limit ?? 10}
              total={totalCount}
              onPageChange={(p) => updateApiParams({ page: p })}
              onLimitChange={(l) => updateApiParams({ limit: l, page: 1 })}
              storageKey="pagination:notifications"
              disabled={loading.notifications}
            />
          </>
        )}
      </PageCard>

      <NotificationDetailModal
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedNotification(null); }}
        notification={selectedNotification}
      />
    </Box>
  );
};

const NotificationsPage: React.FC = () => (
  <DashboardLayout>
    <NotificationsPageContent />
  </DashboardLayout>
);

export default NotificationsPage;
